import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { getHospitalTabsAnchorElement, getHospitalTabsScrollTop } from "@/widgets/hospital/lib/scrollToHospitalTabs";
import { getStickyHeaderHeight } from "@/shared/utils/getStickyHeaderHeight";
const TAB_KEYS = ["photo", "introduction", "doctor", "location", "treatment"] as const;
export type PartnerHospitalTabKey = (typeof TAB_KEYS)[number];
export const PARTNER_HOSPITAL_TAB_DEFS: ReadonlyArray<{
  value: PartnerHospitalTabKey;
  messageKey: string;
}> = [{
  value: "photo",
  messageKey: "partnerTabs.photo"
}, {
  value: "introduction",
  messageKey: "partnerTabs.introduction"
}, {
  value: "doctor",
  messageKey: "partnerTabs.doctor"
}, {
  value: "location",
  messageKey: "partnerTabs.location"
}, {
  value: "treatment",
  messageKey: "partnerTabs.treatment"
}];
const TAB_ID_MAP: Record<PartnerHospitalTabKey, string> = {
  photo: "hospital-images",
  introduction: "hospital-introduction",
  doctor: "hospital-doctor",
  location: "hospital-location",
  treatment: "hospital-treatment"
};
const ARRIVAL_EPSILON_PX = 4;
const CLICK_SCROLL_UNLOCK_MS = 1400;
type ClickScrollState = {
  isLocked: boolean;
  pendingTab: PartnerHospitalTabKey | null;
  pendingTargetId: string | null;
  unlockTimer: ReturnType<typeof setTimeout> | null;
};
const createInitialClickScrollState = (): ClickScrollState => ({
  isLocked: false,
  pendingTab: null,
  pendingTargetId: null,
  unlockTimer: null
});
const getContentTabsHeight = () => document.getElementById("hospital-tabs")?.getBoundingClientRect().height ?? 0;
const getHospitalTabsStickyTop = () => getStickyHeaderHeight() + getContentTabsHeight();
const getSectionElement = (tab: PartnerHospitalTabKey) => document.getElementById(TAB_ID_MAP[tab]);
const getSectionDocumentTop = (element: HTMLElement) => element.getBoundingClientRect().top + window.scrollY;
const isHospitalTabsAnchor = (element: HTMLElement) => element.id === "hospital-tabs-anchor" || element.id === "hospital-tabs";
const getDesiredScrollTop = (element: HTMLElement, getTopOffsetPx: () => number): number | null => {
  if (isHospitalTabsAnchor(element)) return getHospitalTabsScrollTop();
  return getSectionDocumentTop(element) - getTopOffsetPx();
};
const findActiveTabFromScroll = (getTopOffsetPx: () => number): PartnerHospitalTabKey | null => {
  const currentY = window.scrollY + getTopOffsetPx() + 1;
  let activeTab: PartnerHospitalTabKey | null = null;
  for (const key of TAB_KEYS) {
    const section = document.getElementById(TAB_ID_MAP[key]);
    if (!section) continue;
    if (getSectionDocumentTop(section) <= currentY) activeTab = key;
  }
  return activeTab;
};
export const usePartnerHospitalTabScrollSync = tabsRef => {
  const $ = _c(18);
  const [selectedTab, setSelectedTab] = useState("photo");
  const [stickyTop, setStickyTop] = useState(52);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = createInitialClickScrollState();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const clickScrollRef = useRef(t0);
  const scrollRafRef = useRef(null);
  const clickRafRef = useRef(null);
  const getTopOffsetPxRef = useRef(_temp);
  let t1;
  if ($[1] !== tabsRef) {
    t1 = () => {
      const tabsEl = tabsRef.current;
      const tabsHeight = tabsEl?.offsetHeight ?? 0;
      const tabsPaddingTop = tabsEl ? Number.parseFloat(getComputedStyle(tabsEl).paddingTop || "0") : 0;
      return getHospitalTabsStickyTop() + tabsHeight - tabsPaddingTop;
    };
    $[1] = tabsRef;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const getTopOffsetPx = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = element => {
      const scrollTop = getDesiredScrollTop(element, getTopOffsetPxRef.current);
      if (scrollTop === null) {
        return;
      }
      window.scrollTo({
        top: scrollTop,
        behavior: "smooth"
      });
      clickScrollRef.current.pendingTargetId = element.id;
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const scrollToTarget = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = tab => {
      const section = getSectionElement(tab);
      if (!section) {
        return;
      }
      scrollToTarget(section);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const scrollToSection = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const anchor = getHospitalTabsAnchorElement();
      if (!anchor) {
        return false;
      }
      scrollToTarget(anchor);
      return true;
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const scrollToHospitalInfoAnchor = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const {
        unlockTimer
      } = clickScrollRef.current;
      if (unlockTimer) {
        clearTimeout(unlockTimer);
      }
      clickScrollRef.current = createInitialClickScrollState();
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const clearClickScrollLock = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = tab_0 => {
      const clickScroll = clickScrollRef.current;
      clickScroll.isLocked = true;
      clickScroll.pendingTab = tab_0;
      if (clickScroll.unlockTimer) {
        clearTimeout(clickScroll.unlockTimer);
      }
      clickScroll.unlockTimer = setTimeout(clearClickScrollLock, CLICK_SCROLL_UNLOCK_MS);
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const startClickScrollLock = t6;
  let t7;
  if ($[8] !== getTopOffsetPx) {
    t7 = () => {
      getTopOffsetPxRef.current = getTopOffsetPx;
    };
    $[8] = getTopOffsetPx;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  useEffect(t7);
  let t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const updateStickyTop = () => setStickyTop(getHospitalTabsStickyTop());
      updateStickyTop();
      window.addEventListener("resize", updateStickyTop);
      return () => window.removeEventListener("resize", updateStickyTop);
    };
    t9 = [];
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      const tryCompleteClickScroll = () => {
        const {
          isLocked,
          pendingTab,
          pendingTargetId
        } = clickScrollRef.current;
        if (!isLocked || !pendingTab || !pendingTargetId) {
          return false;
        }
        const targetSection = document.getElementById(pendingTargetId);
        if (!targetSection) {
          return true;
        }
        const desiredScrollTop = getDesiredScrollTop(targetSection, getTopOffsetPxRef.current);
        if (desiredScrollTop === null) {
          return true;
        }
        const hasArrived = Math.abs(window.scrollY - desiredScrollTop) <= ARRIVAL_EPSILON_PX;
        if (!hasArrived) {
          return true;
        }
        window.scrollTo({
          top: desiredScrollTop,
          behavior: "auto"
        });
        setSelectedTab(prev => prev === pendingTab ? prev : pendingTab);
        clearClickScrollLock();
        return true;
      };
      const syncTabFromScrollPosition = () => {
        const nextActive = findActiveTabFromScroll(getTopOffsetPxRef.current);
        if (nextActive === null) {
          return;
        }
        setSelectedTab(prev_0 => prev_0 === nextActive ? prev_0 : nextActive);
      };
      const updateActiveTab = () => {
        if (tryCompleteClickScroll()) {
          return;
        }
        syncTabFromScrollPosition();
      };
      const scheduleActiveTabUpdate = () => {
        if (scrollRafRef.current) {
          cancelAnimationFrame(scrollRafRef.current);
        }
        scrollRafRef.current = requestAnimationFrame(updateActiveTab);
      };
      updateActiveTab();
      window.addEventListener("scroll", scheduleActiveTabUpdate, {
        passive: true
      });
      window.addEventListener("resize", scheduleActiveTabUpdate);
      return () => {
        window.removeEventListener("scroll", scheduleActiveTabUpdate);
        window.removeEventListener("resize", scheduleActiveTabUpdate);
        if (scrollRafRef.current) {
          cancelAnimationFrame(scrollRafRef.current);
        }
        if (clickRafRef.current) {
          cancelAnimationFrame(clickRafRef.current);
        }
        if (clickScrollRef.current.unlockTimer) {
          clearTimeout(clickScrollRef.current.unlockTimer);
        }
      };
    };
    t11 = [];
    $[12] = t10;
    $[13] = t11;
  } else {
    t10 = $[12];
    t11 = $[13];
  }
  useEffect(t10, t11);
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = tab_1 => {
      const section_0 = getSectionElement(tab_1);
      if (!section_0) {
        setSelectedTab(tab_1);
        startClickScrollLock(tab_1);
        if (!scrollToHospitalInfoAnchor()) {
          clearClickScrollLock();
        }
        return;
      }
      startClickScrollLock(tab_1);
      if (clickRafRef.current) {
        cancelAnimationFrame(clickRafRef.current);
      }
      clickRafRef.current = requestAnimationFrame(() => {
        clickRafRef.current = requestAnimationFrame(() => scrollToSection(tab_1));
      });
    };
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const handleTabClick = t12;
  let t13;
  if ($[15] !== selectedTab || $[16] !== stickyTop) {
    t13 = {
      selectedTab,
      stickyTop,
      handleTabClick
    };
    $[15] = selectedTab;
    $[16] = stickyTop;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  return t13;
};
function _temp() {
  return 0;
}